import {isServer} from '@sail/engine';
import {useEffect, useRef, useState} from 'react';

const visualViewport = !isServer ? window.visualViewport : undefined;

function getVisualViewportSize() {
  return {
    // eslint-disable-next-line @sail/ui-no-reflow
    height: visualViewport?.height || window.innerHeight,
    // eslint-disable-next-line @sail/ui-no-reflow
    offsetTop: visualViewport?.offsetTop || 0,
  };
}

export function useVisualViewportArea() {
  const [size, setSize] = useState(() =>
    isServer ? {height: 0, offsetTop: 0} : getVisualViewportSize(),
  );

  /**
   * Value is 'active' if the pointer is down, or the timestamp of the last pointer up or cancel event.
   */
  const pointers = useRef(new Map<number, 'active' | number>());

  useEffect(() => {
    const updateSize = () => {
      if (
        Array.from(pointers.current.values()).some(
          (value) => value === 'active' || value >= Date.now() - 2,
        )
      ) {
        // We avoid updating the layout before the click event is fired.
        // Otherwise, it would fire on the wrong element.
        // `click` fires in the same task as `pointerup`, so waiting 2ms is enough.
        return;
      }

      setSize((oldSize) => {
        const newSize = getVisualViewportSize();
        if (
          newSize.height === oldSize.height &&
          // eslint-disable-next-line @sail/ui-no-reflow
          newSize.offsetTop === oldSize.offsetTop
        ) {
          return oldSize;
        }

        return newSize;
      });
    };

    const onPointerDown = (e: PointerEvent) => {
      pointers.current.set(e.pointerId, 'active');
    };

    const onPointerUpOrCancel = (e: PointerEvent) => {
      pointers.current.set(e.pointerId, Date.now());

      // Wait for the click event to be fired before updating the size
      setTimeout(() => {
        updateSize();
      }, 4);
    };

    visualViewport?.addEventListener('resize', updateSize);
    visualViewport?.addEventListener('scroll', updateSize);
    window?.addEventListener('pointerdown', onPointerDown, true);
    window?.addEventListener('pointerup', onPointerUpOrCancel, true);
    window?.addEventListener('pointercancel', onPointerUpOrCancel, true);

    return () => {
      visualViewport?.removeEventListener('resize', updateSize);
      visualViewport?.removeEventListener('scroll', updateSize);
      window?.removeEventListener('pointerdown', onPointerDown, true);
      window?.removeEventListener('pointerup', onPointerUpOrCancel, true);
      window?.removeEventListener('pointercancel', onPointerUpOrCancel, true);
    };
  }, []);

  return size;
}
